// PotreeViewer3 리팩토링 - 트랙터 애니메이션 전용 훅
// 역할:
// - 트랙터 Mesh/Group 생성 및 경로 따라 이동 애니메이션 실행
// - requestAnimationFrame 관리 (중복 실행 방지)
// - 컴포넌트 언마운트 시 애니메이션/오브젝트 정리

import { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * useTractorAnimation
 *
 * @param {any} potreeViewer
 * @param {Function} getMinZ - 최소 z값을 반환하는 함수
 * @param {Object} vehicleSpecs - AutovehicleSpecsStore에서 선택된 차량 스펙
 * @param {React.MutableRefObject} selectedStartPointRef - 출발점 정보 ref
 *
 * @returns {{ runAnimation: (fullPath: any) => void }}
 */
export function useTractorAnimation(
  potreeViewer,
  getMinZ,
  vehicleSpecs,
  selectedStartPointRef
) {
  const tractorRef = useRef(null);
  const rafRef = useRef(null);
  const runIdRef = useRef(0);

  // 공통 정리 함수 (원본 stopAnimation)
  const stopAnimation = (scene) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (tractorRef.current) {
      // tractorRef는 Group(권장) 또는 Mesh(구버전)일 수 있어 안전하게 정리
      scene.remove(tractorRef.current);
      const obj = tractorRef.current;
      // Group일 경우 내부 Mesh 자원 정리
      if (obj?.traverse) {
        obj.traverse((child) => {
          child.geometry?.dispose?.();
          child.material?.dispose?.();
        });
      } else {
        // Mesh일 경우
        obj.geometry?.dispose?.();
        obj.material?.dispose?.();
      }
      tractorRef.current = null;
    }
  };

  // 컴포넌트 언마운트 시 안전 정리 (원본 useEffect cleanup)
  useEffect(() => {
    return () => {
      const scene = potreeViewer?.scene?.scene;
      if (scene) stopAnimation(scene);
    };
  }, [potreeViewer]);

  // 원본 GoAnimation(fullPath, getMinZ)를 그대로 옮김
  const runAnimation = (fullPath) => {
    if (!potreeViewer || !fullPath) return;

    const scene = potreeViewer.scene.scene;

    stopAnimation(scene);

    const myRunId = runIdRef.current + 1;
    runIdRef.current = myRunId;

    const path = fullPath.features.flatMap((f) => f.geometry.coordinates);

    // 출발점의 z값 기준 사용
    const baseZ = selectedStartPointRef.current?.z ?? getMinZ();
    const offsetZ = baseZ + 2.5; // 출발점 z값 + 2.5m

    /**
     * ✅ "잠깐 수직으로 서는" 현상 방지:
     * - lookAt()는 세그먼트 전환 시 up/roll이 튀는 경우가 있어,
     *   지면에서의 yaw(heading)만 적용하도록 Group + 고정 메쉬 구조로 변경합니다.
     * - 내부 메쉬는 항상 눕혀진 상태로 고정하고, 그룹만 Z축 회전(yaw)합니다.
     */
    const geometry = new THREE.BoxGeometry(
      vehicleSpecs.vehicleLength, // X: 진행방향(길이)
      vehicleSpecs.width, // Y: 폭
      1.5 // Z: 높이
    );
    const material = new THREE.MeshBasicMaterial({
      color: 0x0080ff,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 1,
    });
    const tractorMesh = new THREE.Mesh(geometry, material);
    tractorMesh.name = "tractor_mesh";

    // Z-up 환경에서 yaw만 적용하기 위해 Group 사용
    const tractor = new THREE.Group();
    tractor.name = "tractor";
    tractor.up.set(0, 0, 1);
    tractor.add(tractorMesh);

    if (path.length > 0) {
      const [x, y] = path[0];
      tractor.position.set(x, y, offsetZ); // 출발점 z값 + 2.5m
    }

    scene.add(tractor);
    tractorRef.current = tractor;

    let i = 0;
    let t = 0;
    const baseSpeed = 40.0; // 프레임당 이동 보간 비율

    const curr = new THREE.Vector3();
    const next = new THREE.Vector3();

    function frame() {
      // 내가 최신 실행이 아니면 종료 (이전 루프 자연 소멸)
      if (runIdRef.current !== myRunId) return;

      if (i < path.length - 1 && tractorRef.current) {
        const [x1, y1] = path[i];
        const [x2, y2] = path[i + 1];

        // 출발점의 z값 기준 사용
        const baseZInner = selectedStartPointRef.current?.z ?? getMinZ();
        const offsetZInner = baseZInner + 2.5; // 출발점 z값 + 2.5m

        curr.set(x1, y1, offsetZInner);
        next.set(x2, y2, offsetZInner);

        const distance = curr.distanceTo(next);
        const speed = distance > 0 ? baseSpeed / (distance * 60) : 0.05;

        t += speed;
        if (t > 1) t = 1;

        tractorRef.current.position.lerpVectors(curr, next, t);

        // heading(yaw)만 적용: z축 회전으로 진행방향 정렬
        const dx = x2 - x1;
        const dy = y2 - y1;
        if (dx !== 0 || dy !== 0) {
          const yaw = Math.atan2(dy, dx);
          tractorRef.current.rotation.set(0, 0, yaw);
        }

        if (t >= 1) {
          i++;
          t = 0;
        }
      } else {
        // 경로 완료 시 정리
        stopAnimation(scene);
        return;
      }

      potreeViewer.render();
      rafRef.current = requestAnimationFrame(frame);
    }

    frame();
  };

  return { runAnimation };
}

